<script>
	import { slugify, safeStringify } from "cando-util";
	import SvelteFa from "$lib/components/SvelteFa.svelte";

	import { detectFieldType } from "./tableUtils.js";

	import AuthorizedExtensions from "./Roles/AuthorizedExtensions.svelte";
	import AuthorizedAccounts from "./Roles/AuthorizedAccounts.svelte";
	import ReadMore from "../../components/ReadMore.svelte";
	import RenderJson from "$lib/components/RenderJSON/RenderJSON.svelte";
	import EmbeddedCalls from "./Management/Cases/EmbeddedCalls.svelte";
	import TextInput from "$lib/components/TextInput.svelte";
	import Textarea from "$lib/components/Textarea.svelte";
	import PhoneNumber from "$lib/components/PhoneNumber.svelte";
	import Currency from "$lib/components/Currency.svelte";
	import Select from "$lib/components/Select.svelte";
	import Events from "./Management/Submissions/Events.svelte";
	import FileUploads from "$lib/components/FormSubmission/FileUploads.svelte";
	import Autocomplete from "$lib/components/Autocomplete.svelte";
	import Tasks from "./Management/Submissions/Tasks.svelte";
	import DeleteModal from "../../components/DeleteModal.svelte";
	import Suggestion from "./Suggestion.svelte";
	import MarkedContent from "$lib/components/MarkedContent.svelte";

	export let fld = {};
	export let col;
	export let docId;
	export let docs;
	export let action;
	export let asBlock = false;
	export let allowEdit = true;

	const containerTag = asBlock ? "div" : "td";

	const getFieldTypeForDoc = (fld, docId) => {
		if (fld.type) return fld.type;
		const record = { __docId: docId, ...docs?.[docId] };
		if (typeof fld.dynamicTypeResolver === "function") {
			return fld.dynamicTypeResolver(record) || undefined;
		}
		if (fld.key) {
			return detectFieldType(fld.key, record?.[fld.key]);
		}
		return undefined;
	};

	const normalizeFileEntries = (entries) => {
		if (!entries) return {};
		if (Array.isArray(entries)) {
			return entries.reduce((acc, file, index) => {
				if (!file) return acc;
				const key = file?.name || `file-${index}`;
				acc[key] = file;
				return acc;
			}, {});
		}
		if (typeof entries === "object") return entries;
		return {};
	};

	const isIdentifierField = (fld) =>
		Boolean(fld?.isIdentifier || fld?.key === "__docId");

	const READ_MORE_TOTAL_LIMIT = 200;
	const READ_MORE_LINE_LIMIT = 80;

	const shouldUseReadMore = (value) => {
		if (typeof value !== "string") return false;
		if (value.includes("<a href=")) return false;
		const trimmed = value.trim();
		if (!trimmed) return false;
		if (trimmed.length > READ_MORE_TOTAL_LIMIT) return true;
		return trimmed
			.split(/\r?\n/)
			.some((line) => line.trim().length > READ_MORE_LINE_LIMIT);
	};

	const loadValue = async (docId, fn) => {
		if (fn === "function") cellValue = await fld.function(docId);
		if (fn === "value") fldValue = await fld.value(docId);
	};

	let cellValue;
	$: if (typeof fld.function === "function" && docId) {
		loadValue(docId, "function");
	}
	let fldValue;
	$: if (typeof fld.value === "function" && docId) {
		loadValue(docId, "value");
	}

	$: isIdColumn = isIdentifierField(fld) && !!action;
	$: useReadMore =
		!isIdColumn &&
		shouldUseReadMore(cellValue) &&
		fld.useReadMore !== false;

	$: key = typeof fld.key === "function" ? fld.key(docId) : fld.key;
	// make sure fld has an action.  Tasks need to check a few keys though so they're excluded
	$: canEdit = typeof fld.action === "function" && allowEdit ? true : false;
</script>

{#if fld.hide && fld.hide?.(docId)}
	<svelte:element this={containerTag}>&nbsp;</svelte:element>
{:else if ["Image", "file"].includes(getFieldTypeForDoc(fld, docId))}
	<svelte:element this={containerTag}>
		<FileUploads
			value={fld.function(docId)}
			label={key}
			readonly={fld.readonly || !fld.action}
			accept={fld.accept}
			action={fld.action}
			{docId}
			{col}
			{key}
			existingDoc={docs[docId]}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "TextObject"}
	<svelte:element this={containerTag} class="align-top">
		<div class="max-h-18 overflow-auto">
			<RenderJson
				strJson={safeStringify(
					docs?.[docId]?.[key]?.value ?? docs?.[docId]?.[key],
				)}
				componentId={`${slugify(fld.heading)}-${slugify(docId)}-textobject`}
				widthClass="w-full max-w-[32rem] overflow-auto"
			/>
		</div>
	</svelte:element>
{:else if getFieldTypeForDoc(fld, docId) === "RenderJson"}
	{@const jsonValue = fld.function(docId)}
	{@const hasJsonValue = !(
		jsonValue === null || typeof jsonValue === "undefined"
	)}
	<svelte:element this={containerTag} class="align-top">
		{#if hasJsonValue}
			<RenderJson
				strJson={typeof jsonValue === "string"
					? jsonValue
					: safeStringify(jsonValue)}
				componentId={fld.renderJsonOptions?.componentId ??
					`${slugify(fld.heading)}-${slugify(docId)}-json`}
				divIdPrefix={fld.renderJsonOptions?.divIdPrefix ?? "tablejson"}
				widthClass={fld.renderJsonOptions?.widthClass ??
					"overflow-auto max-w-[40rem]"}
				spacingClass={fld.renderJsonOptions?.spacingClass ?? "p-0"}
				borderClass={fld.renderJsonOptions?.borderClass ??
					"border border-primary/50"}
				otherClass={fld.renderJsonOptions?.otherClass ??
					"rounded bg-base-200 text-xs"}
				isExpanded={fld.renderJsonOptions?.isExpanded ?? false}
			/>
		{:else}
			&nbsp;
		{/if}
	</svelte:element>
{:else if canEdit && fld.type === "checkbox"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<input
			type="checkbox"
			id={`${slugify(`${containerTag} ${docId} ${key} ${fld.type} input`)}`}
			checked={fldValue}
			{docId}
			itemId={docId}
			value={key}
			class="checkbox checkbox-primary"
			{key}
			on:change={fld.action}
		/>
		<span class={`label-text ml-2 text-left`}>{fld.heading}</span>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && fld.type === "date"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<input
			type="date"
			id={`${slugify(`${containerTag} ${docId} ${key} ${fld.type} input`)}`}
			{docId}
			itemId={docId}
			{key}
			class={`w-full input input-bordered text-lg sm:text-xl`}
			name={fld.heading}
			value={fldValue}
			on:change={fld.action}
			readonly={fld.readonly}
			disabled={fld.disabled && fld.disabled(docId)}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && fld.type === "datetime"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<input
			type="datetime-local"
			id={`${slugify(`${containerTag} ${docId} ${key} ${fld.type} input`)}`}
			{docId}
			itemId={docId}
			{key}
			class={`w-full input input-bordered text-lg sm:text-xl`}
			name={fld.heading}
			value={fldValue}
			on:change={fld.action}
			readonly={fld.readonly}
			disabled={fld.disabled && fld.disabled(docId)}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && ["dropdown", "select"].includes(fld.type)}
	<svelte:element this={containerTag} class="cursor-pointer">
		<Select
			value={fldValue}
			name={`${containerTag} ${docId} ${key} ${fld.type} input`}
			{key}
			options={typeof fld.options === "function"
				? fld.options(docId)
				: fld.options}
			handleChange={fld.action}
			readonly={fld.readonly}
			disabled={fld.disabled && fld.disabled(docId)}
			itemId={docId}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && ["autocomplete"].includes(fld.type)}
	<svelte:element this={containerTag}>
		<div>
			<Autocomplete
				value={fldValue}
				options={typeof fld.options === "function"
					? fld.options(docId)
					: fld.options}
				handleChange={fld.action}
				name={`${containerTag} ${docId} ${key} ${fld.type} input`}
				{key}
				itemId={docId}
				disabled={fld.disabled && fld.disabled(docId)}
			/>
		</div>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if fld.type === "Tasks"}
	<svelte:element this={containerTag}>
		<Tasks
			tasks={fldValue}
			{col}
			{docId}
			{key}
			moveAction={allowEdit ? fld.moveAction : false}
			selectAction={allowEdit ? fld.selectAction : false}
			existingDoc={docs[docId]}
		/>
	</svelte:element>
{:else if canEdit && ["text", "email"].includes(fld.type)}
	<svelte:element this={containerTag} class="cursor-pointer">
		<TextInput
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${fld.type}`}
			{key}
			handleInputChange={fld.action}
			itemId={docId}
			cls={"input input-bordered input-primary w-full"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && ["textarea"].includes(fld.type)}
	<svelte:element this={containerTag} class="cursor-pointer">
		<Textarea
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${fld.type}`}
			{key}
			handleInputChange={fld.action}
			itemId={docId}
			additionalClass={"input input-bordered input-primary text-wrap"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && fld.type === "phone"}
	<svelte:element this={containerTag} class="block cursor-pointer">
		<PhoneNumber
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${fld.type}`}
			{key}
			handleChange={fld.action}
			itemId={docId}
			cls={"input input-bordered input-primary"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && fld.type === "currency"}
	<svelte:element this={containerTag} class="cursor-pointer">
		<Currency
			value={fldValue}
			label={`${containerTag} ${docId} ${key} ${fld.type} input`}
			{key}
			handleInputChange={fld.action}
			itemId={docId}
			cls={"input input-bordered input-primary w-full"}
		/>
		<Suggestion {docId} {fld} />
	</svelte:element>
{:else if canEdit && fld.type === "button" && !!fld.label(docId)}
	<svelte:element this={containerTag}>
		<button
			class={`${fld.class || "btn btn-primary btn-sm"}`}
			disabled={fld.disabled && fld.disabled(docId)}
			on:click={fld.action}
			{docId}
			itemId={docId}
		>
			{#if fld.icon}
				<SvelteFa icon={fld.icon} size="1x" />
			{/if}
			{#if fld.label}
				{fld.label(docId)}
			{/if}
		</button>
	</svelte:element>
{:else if canEdit && fld.type === "DeleteModal"}
	<DeleteModal
		colDtls={{ name: fld.name, singular: fld.singular }}
		obj={fldValue}
		deleteFunction={fld.action}
		closeModalOnDelete={fld.closeModalOnDelete}
		btnCls={`${fld.btnCls || "w-full btn btn-warning rounded-btn modal-button"}`}
		disabled={fld.disabled && fld.disabled(docId)}
		redirectLink={fld.redirectLink}
	/>
{:else if fld.type === "AuthorizedExtensions"}
	<svelte:element this={containerTag}>
		<AuthorizedExtensions email={docId} />
	</svelte:element>
{:else if fld.type === "AuthorizedAccounts"}
	<svelte:element this={containerTag}>
		<AuthorizedAccounts email={docId} />
	</svelte:element>
{:else if fld.type === "ReadMore"}
	<svelte:element this={containerTag} class="text-wrap">
		<ReadMore content={fld.function(docId)} previewHeightClass="max-h-18" />
	</svelte:element>
{:else if fld.type === "EmbeddedCalls"}
	<svelte:element this={containerTag} class="text-wrap">
		<EmbeddedCalls calls={fld.function(docId)} />
	</svelte:element>
{:else if fld.type === "Events"}
	<svelte:element this={containerTag} class="text-wrap">
		<Events
			events={fld.function(docId)}
			{col}
			{docId}
			{key}
			action={fld.action}
			existingDoc={docs[docId]}
		/>
	</svelte:element>
{:else if fld.type === "MarkedContent"}
	<svelte:element this={containerTag} class="text-wrap">
		<MarkedContent markedContent={fldValue} />
	</svelte:element>
{:else}
	<svelte:element
		this={containerTag}
		class={`${useReadMore ? "text-wrap align-top" : "text-nowrap align-top"}`}
	>
		{#if isIdColumn}
			<button
				class="link underline"
				type="button"
				on:click|stopPropagation={() => action(docId)}
			>
				{@html cellValue}
			</button>
		{:else if useReadMore}
			<ReadMore content={cellValue} previewHeightClass="max-h-18" />
		{:else}
			{@html cellValue}
		{/if}
	</svelte:element>
{/if}
